import type { StatusTone } from '@/components/shell/StatusChip';
import type { GatewayStatus, PaymentIntentStatus, PaymentOperationStatus } from '@/api/models';

/**
 * Maps the contract's forward-only status machine to one of the theme's
 * five badge tones (foundations.md §7.8). `refunded`/`partially_refunded`
 * are `warning`, not `success`: money left the tenant's account again.
 */
export function paymentStatusTone(status: PaymentIntentStatus): StatusTone {
  switch (status) {
    case 'captured':
    case 'authorized':
      return 'success';
    case 'refunded':
    case 'partially_refunded':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'redirected':
    case 'session_consumed':
      return 'info';
    case 'created':
    case 'cancelled':
    case 'expired':
    default:
      return 'neutral';
  }
}

/** Maps a `PaymentOperation.status` to a badge tone. */
export function operationStatusTone(status: PaymentOperationStatus): StatusTone {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'danger';
    case 'pending':
    default:
      return 'neutral';
  }
}

/** Presentation-only capitalization of a contract enum value (e.g. a `gateway`). Never renames it. */
export function capitalizeValue(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The three situations a hotel needs to tell apart at a glance, read from
 * `gatewayStatus`/`gatewayStatusCheckedAt` together — never from
 * `gatewaySupported`, which only says whether the gateway can be asked at
 * all, not whether *this* intent was asked:
 * - `neverChecked`: both fields are `null` — nobody has asked yet.
 * - `unverifiable`: it was asked (`gatewayStatusCheckedAt` set) but the
 *   gateway gave no confirmable answer (`gatewayStatus` still `null`).
 * - `confirmed`: it was asked and answered; `gatewayStatus` itself names
 *   the funnel situation (`awaiting_guest`, `attempt_declined`,
 *   `action_required`, `paid`).
 */
export type GatewayCheckSituation = 'neverChecked' | 'unverifiable' | 'confirmed';

export function gatewayCheckSituation(
  gatewayStatus: GatewayStatus | null,
  gatewayStatusCheckedAt: string | null,
): GatewayCheckSituation {
  if (gatewayStatusCheckedAt === null) return 'neverChecked';
  return gatewayStatus === null ? 'unverifiable' : 'confirmed';
}

/** Maps a confirmed `GatewayStatus` to a badge tone. `paid` is the only good news. */
export function gatewayStatusTone(status: GatewayStatus): StatusTone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'attempt_declined':
      return 'danger';
    case 'action_required':
      return 'warning';
    case 'awaiting_guest':
    default:
      return 'neutral';
  }
}
